import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { LocalDate } from "@/components/ui/local-date";
import { PAGE_SIZE_OPTIONS, Pagination } from "@/components/ui/pagination";
import { ApiError, api } from "@/lib/api";
import { paginationSearchFields } from "@/lib/search";
import { useApiMutation } from "@/lib/use-api-mutation";
import { createFileRoute, redirect, useNavigate, useRouter } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { z } from "zod";

/**
 * `/accounting/bank` — the statement and the payments inbox.
 *
 * Automatic matching only acts on a variable symbol in the document's
 * own currency, so what lands here is what a person has to decide:
 * payments with no symbol, symbols that match nothing, foreign-currency
 * payments, and movements that are not document payments at all. For
 * each one Hub offers what the payment could be — by symbol, by amount,
 * and by who has paid from that account before — but never places it
 * on its own.
 */

const searchSchema = z.object({
  view: z.enum(["inbox", "all", "ignored"]).optional(),
  ...paginationSearchFields,
});

type Match = {
  id: string;
  amount: string;
  source: "auto" | "manual" | "fakturoid";
  /** `false` = the movement only evidences the document; it was already paid in Fakturoid. */
  settles: boolean;
  issuedInvoice: { id: string; number: string; partnerName: string } | null;
  receivedInvoice: {
    id: string;
    number: string | null;
    internalNumber: string | null;
    partnerName: string;
  } | null;
};

type Transaction = {
  id: string;
  bookedOn: string;
  amount: string;
  currency: string;
  variableSymbol: string | null;
  counterpartyName: string | null;
  counterpartyAccount: string | null;
  counterpartyBankCode: string | null;
  userIdentification: string | null;
  message: string | null;
  ignoredAt: string | null;
  ignoreReason: string | null;
  /** Put aside by a rule rather than by a person. */
  ignoredByRuleId: string | null;
  account: { label: string };
  matches: Match[];
  /** Merchant and the charge before conversion, read from a card payment's text. */
  card: { merchant: string; amount: string; currency: string } | null;
};

type Candidate = {
  kind: "issued" | "received";
  id: string;
  number: string;
  partnerName: string;
  total: string;
  currency: string;
  status: string;
  reasons: Array<
    "variable_symbol" | "exact_amount" | "known_counterparty" | "card_amount" | "supplier_name"
  >;
};

type SyncResult = {
  accounts?: number;
  imported?: number;
  matched?: number;
  unmatched?: number;
  skipped?: Array<{ account: string; reason: string }>;
  logs?: string[];
};

type BankStatus = {
  lastRun: {
    status: "pending" | "running" | "completed" | "failed";
    startedAt: string | null;
    finishedAt: string | null;
    error: string | null;
    result: SyncResult | null;
  } | null;
  accountsWithToken: number;
};

type BankAccountView = {
  id: string;
  label: string;
  accountNumber: string;
  currency: string;
  pairingEnabled: boolean;
  syncMode: "window" | "bookmark";
  lastFetchedAt: string | null;
  hasToken: boolean;
};

const REASON_LABEL: Record<Candidate["reasons"][number], string> = {
  variable_symbol: "variable symbol",
  exact_amount: "exact amount",
  known_counterparty: "pays from this account",
  card_amount: "card amount",
  supplier_name: "same supplier",
};

export const Route = createFileRoute("/_app/accounting/bank")({
  validateSearch: searchSchema,
  loaderDeps: ({ search }) => ({
    view: search.view,
    page: search.page ?? 1,
    pageSize: search.pageSize ?? 50,
  }),
  loader: async ({ deps }) => {
    const view = deps.view ?? "inbox";
    const query = view === "inbox" ? "&matched=false" : view === "ignored" ? "&ignored=true" : "";
    try {
      const [movements, accounts, status] = await Promise.all([
        api.get<{ transactions: Transaction[]; total: number; page: number; pageSize: number }>(
          `/api/accounting/bank/transactions?page=${deps.page}&pageSize=${deps.pageSize}${query}`,
        ),
        api.get<{ accounts: BankAccountView[] }>("/api/accounting/bank/accounts"),
        api.get<BankStatus>("/api/accounting/bank/status"),
      ]);
      return { ...movements, accounts: accounts.accounts, status, view };
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) throw redirect({ to: "/my" });
      throw err;
    }
  },
  component: BankPage,
});

function money(amount: string, currency: string): string {
  return `${Number(amount).toLocaleString("cs-CZ", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency}`;
}

function BankPage() {
  const { transactions, total, page, pageSize, accounts, status, view } = Route.useLoaderData();
  const search = Route.useSearch();
  // Pulling and backfilling only make sense where there is something to
  // read. Without a token both would just occupy the queue and return
  // nothing.
  const canPull = status.accountsWithToken > 0;
  const navigate = useNavigate();
  const router = useRouter();
  const [open, setOpen] = useState<string | null>(null);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const pull = useApiMutation(
    (body: { from?: string; to?: string }) => api.post("/api/accounting/bank/sync/run", body),
    { onSuccess: () => router.invalidate(), errorMessage: () => "Couldn't queue the pull." },
  );

  const [rematched, setRematched] = useState<{
    checked: number;
    matched: number;
    released: number;
    restored: number;
  } | null>(null);
  const rematch = useApiMutation(
    () =>
      api.post<{ checked: number; matched: number; released: number; restored: number }>(
        "/api/accounting/bank/rematch",
        {},
      ),
    {
      onSuccess: (result) => {
        setRematched(result);
        router.invalidate();
      },
      errorMessage: () => "Couldn't match the inbox again.",
    },
  );

  const addAccount = useApiMutation(
    (body: Record<string, unknown>) => api.post("/api/accounting/bank/accounts", body),
    { onSuccess: () => router.invalidate(), errorMessage: () => "Couldn't add the account." },
  );

  const removeAccount = useApiMutation(
    (id: string) => api.delete(`/api/accounting/bank/accounts/${id}`),
    { onSuccess: () => router.invalidate(), errorMessage: () => "Couldn't remove the account." },
  );

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-semibold text-2xl">Bank</h1>
          <p className="text-muted-foreground text-sm">
            Movements arrive on their own and settle the invoice whose variable symbol they carry.
            What needs a decision is here.
          </p>
        </div>
        <Button
          variant="outline"
          disabled={pull.pending || !canPull}
          title={canPull ? undefined : "Add an account with a Fio token first"}
          onClick={() => pull.mutate({})}
        >
          {pull.pending ? "Queueing…" : "Pull now"}
        </Button>
      </div>

      {pull.error && <p className="text-destructive text-sm">{pull.error}</p>}
      {removeAccount.error && <p className="text-destructive text-sm">{removeAccount.error}</p>}

      <LastPull status={status} accountCount={accounts.length} />

      <Card>
        <CardHeader>
          <CardTitle>Accounts</CardTitle>
          <CardDescription>
            Use a Fio token of the read-only ("sledovat účet") kind — read access, never a payment
            token — and issue it for Hub alone. A bookmark read empties Fio's queue for that token,
            so a token shared with another system means the two of you split the movements between
            you.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {accounts.length === 0 ? (
            <p className="text-muted-foreground text-sm">No bank account yet.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-left text-muted-foreground">
                <tr>
                  <th className="py-1 pr-4 font-medium">Account</th>
                  <th className="py-1 pr-4 font-medium">Number</th>
                  <th className="py-1 pr-4 font-medium">Token</th>
                  <th className="py-1 pr-4 font-medium">Reads</th>
                  <th className="py-1 pr-4 font-medium">Auto-matching</th>
                  <th className="py-1 pr-4 font-medium">Last pull</th>
                  <th className="py-1 font-medium" />
                </tr>
              </thead>
              <tbody>
                {accounts.map((account) => (
                  <tr key={account.id} className="border-t">
                    <td className="py-1 pr-4">{account.label}</td>
                    <td className="py-1 pr-4 font-mono text-xs">{account.accountNumber}</td>
                    <td className="py-1 pr-4">
                      <TokenCell account={account} onDone={() => router.invalidate()} />
                    </td>
                    <td className="py-1 pr-4 text-muted-foreground">
                      {account.syncMode === "window" ? "last 30 days" : "since bookmark"}
                    </td>
                    <td className="py-1 pr-4">{account.pairingEnabled ? "on" : "off"}</td>
                    <td className="py-1 pr-4 text-muted-foreground">
                      {account.lastFetchedAt ? (
                        <LocalDate value={account.lastFetchedAt} withTime />
                      ) : (
                        "never"
                      )}
                    </td>
                    <td className="py-1 text-right">
                      <button
                        type="button"
                        className="text-destructive text-xs underline"
                        disabled={removeAccount.pending}
                        onClick={() => removeAccount.mutate(account.id)}
                      >
                        remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <form
            className="grid gap-3 border-t pt-4 sm:grid-cols-6"
            onSubmit={(event) => {
              event.preventDefault();
              const fd = new FormData(event.currentTarget);
              const text = (key: string) => ((fd.get(key) as string) ?? "").trim();
              addAccount.mutate({
                label: text("label"),
                accountNumber: text("accountNumber"),
                bankCode: text("bankCode") || null,
                currency: text("currency") || "CZK",
                // The token goes to the server in this one request only;
                // it never comes back.
                fioToken: text("fioToken") || null,
              });
              event.currentTarget.reset();
            }}
          >
            <div className="sm:col-span-2">
              <label htmlFor="label" className="text-muted-foreground text-xs">
                Account name
              </label>
              <input
                id="label"
                name="label"
                required
                placeholder="Fio — operations"
                className="block h-9 w-full rounded-md border border-input bg-background px-2 text-sm"
              />
            </div>
            <div className="sm:col-span-2">
              <label htmlFor="accountNumber" className="text-muted-foreground text-xs">
                Account number
              </label>
              <input
                id="accountNumber"
                name="accountNumber"
                required
                placeholder="2900233333"
                className="block h-9 w-full rounded-md border border-input bg-background px-2 text-sm"
              />
            </div>
            <div>
              <label htmlFor="bankCode" className="text-muted-foreground text-xs">
                Kód banky
              </label>
              <input
                id="bankCode"
                name="bankCode"
                placeholder="2010"
                className="block h-9 w-full rounded-md border border-input bg-background px-2 text-sm"
              />
            </div>
            <div>
              <label htmlFor="currency" className="text-muted-foreground text-xs">
                Currency
              </label>
              <input
                id="currency"
                name="currency"
                defaultValue="CZK"
                className="block h-9 w-full rounded-md border border-input bg-background px-2 text-sm"
              />
            </div>
            <div className="sm:col-span-5">
              <label htmlFor="fioToken" className="text-muted-foreground text-xs">
                Fio API token (read-only, 64 characters)
              </label>
              <input
                id="fioToken"
                name="fioToken"
                type="password"
                autoComplete="off"
                placeholder="stored encrypted, never shown again"
                className="block h-9 w-full rounded-md border border-input bg-background px-2 font-mono text-sm"
              />
            </div>
            <div className="flex items-end">
              <Button type="submit" className="w-full" disabled={addAccount.pending}>
                {addAccount.pending ? "Saving…" : "Add account"}
              </Button>
            </div>
            {addAccount.error && (
              <p className="text-destructive text-sm sm:col-span-6">{addAccount.error}</p>
            )}
          </form>

          <div className="flex flex-wrap items-end gap-2 border-t pt-4">
            <div>
              <label htmlFor="backfill-from" className="text-muted-foreground text-xs">
                Backfill from
              </label>
              <input
                id="backfill-from"
                type="date"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
                className="block h-9 rounded-md border border-input bg-background px-2 text-sm"
              />
            </div>
            <div>
              <label htmlFor="backfill-to" className="text-muted-foreground text-xs">
                to
              </label>
              <input
                id="backfill-to"
                type="date"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                className="block h-9 rounded-md border border-input bg-background px-2 text-sm"
              />
            </div>
            <Button
              variant="outline"
              disabled={!from || !to || pull.pending || !canPull}
              title={canPull ? undefined : "Add an account with a Fio token first"}
              onClick={() => pull.mutate({ from, to })}
            >
              Fetch history
            </Button>
            <p className="text-muted-foreground text-xs">
              Fio serves the last 90 days straight away. For anything older, unlock the token in the
              internet banking first (Settings → API, the padlock) — the history then stays open for
              ten minutes.
            </p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>
              {view === "inbox"
                ? "Needs a decision"
                : view === "ignored"
                  ? "Put aside"
                  : "All movements"}
            </CardTitle>
            <CardDescription>{total} movement(s)</CardDescription>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {view === "inbox" && (
              <>
                {rematched && (
                  <span className="text-muted-foreground text-xs">
                    {rematched.matched} of {rematched.checked} placed
                    {rematched.released > 0 &&
                      `, ${rematched.released} placed again by amount (shared symbol)`}
                    {rematched.restored > 0 && `, ${rematched.restored} lost payment(s) restored`}
                  </span>
                )}
                {rematch.error && <span className="text-destructive text-xs">{rematch.error}</span>}
                <Button
                  variant="outline"
                  size="sm"
                  disabled={rematch.pending}
                  onClick={() => rematch.mutate()}
                >
                  {rematch.pending ? "Matching…" : "Match again"}
                </Button>
              </>
            )}
            {(["inbox", "all", "ignored"] as const).map((option) => (
              <Button
                key={option}
                variant={view === option ? "default" : "outline"}
                size="sm"
                onClick={() => navigate({ to: "/accounting/bank", search: { view: option } })}
              >
                {option === "inbox" ? "Inbox" : option === "all" ? "All" : "Put aside"}
              </Button>
            ))}
          </div>
        </CardHeader>
        <CardContent>
          {transactions.length === 0 ? (
            <p className="text-muted-foreground text-sm">
              {view === "inbox"
                ? "Nothing waiting — every movement is placed or put aside."
                : "Nothing here."}
            </p>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-left text-muted-foreground">
                <tr>
                  <th className="py-1 pr-4 font-medium">Date</th>
                  <th className="py-1 pr-4 font-medium">Counterparty</th>
                  <th className="py-1 pr-4 font-medium">VS</th>
                  <th className="py-1 pr-4 text-right font-medium">Amount</th>
                  <th className="py-1 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map((movement) => (
                  <MovementRow
                    key={movement.id}
                    movement={movement}
                    isOpen={open === movement.id}
                    onToggle={() => setOpen(open === movement.id ? null : movement.id)}
                    onDone={() => {
                      setOpen(null);
                      router.invalidate();
                    }}
                  />
                ))}
              </tbody>
            </table>
          )}
          <Pagination
            total={total}
            page={page}
            pageSize={pageSize}
            pageSizes={PAGE_SIZE_OPTIONS}
            storageKey="accounting-bank"
            basePath="/accounting/bank"
            searchParams={{ view: search.view, pageSize: search.pageSize }}
          />
        </CardContent>
      </Card>

      <PaidCheck />

      <RebuildFromBank />

      <IgnoreRules />
    </div>
  );
}

type CheckRow = {
  kind: "issued" | "received";
  id: string;
  number: string;
  partnerName: string;
  total: string;
  currency: string;
  paidOn: string | null;
  verdict: "tied" | "fits" | "no_movement";
  movements: Array<{
    id: string;
    bookedOn: string;
    amount: string;
    currency: string;
    text: string | null;
  }>;
};

/**
 * Whether the bank backs up what the books call paid. Fakturoid paired
 * its own payments; a document it calls paid with nothing on the account
 * that fits is paid in cash, from elsewhere, or paired wrongly.
 */
function PaidCheck() {
  const [year, setYear] = useState(new Date().getFullYear());
  const [rows, setRows] = useState<CheckRow[] | null>(null);

  const run = useApiMutation(
    () => api.get<{ rows: CheckRow[] }>(`/api/accounting/bank/check?year=${year}`),
    { onSuccess: (result) => setRows(result.rows), errorMessage: () => "Couldn't run the check." },
  );
  const tie = useApiMutation(
    ({ row, movementId }: { row: CheckRow; movementId: string }) =>
      api.post(
        `/api/accounting/bank/transactions/${movementId}/match`,
        row.kind === "received" ? { receivedInvoiceId: row.id } : { issuedInvoiceId: row.id },
      ),
    {
      onSuccess: async () => {
        await run.mutate();
      },
      errorMessage: () => "Couldn't tie the movement.",
    },
  );

  const count = (verdict: CheckRow["verdict"]) =>
    rows?.filter((r) => r.verdict === verdict).length ?? 0;
  const open = rows?.filter((r) => r.verdict !== "tied") ?? [];

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-4">
        <div>
          <CardTitle>Paid in the books, seen in the bank?</CardTitle>
          <CardDescription>
            Every document paid in the year against the account. What Fakturoid calls paid with
            nothing on the account that fits is paid from elsewhere, or paired wrongly.
          </CardDescription>
        </div>
        <div className="flex items-center gap-2">
          <input
            type="number"
            aria-label="Year"
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            className="h-9 w-24 rounded-md border border-input bg-background px-2 text-sm"
          />
          <Button variant="outline" size="sm" disabled={run.pending} onClick={() => run.mutate()}>
            {run.pending ? "Checking…" : "Check"}
          </Button>
        </div>
      </CardHeader>
      {(rows || run.error) && (
        <CardContent className="space-y-3">
          {run.error && <p className="text-destructive text-sm">{run.error}</p>}
          {rows && (
            <p className="text-sm">
              <span className="font-medium">{count("tied")}</span> tied to a movement ·{" "}
              <span className="font-medium">{count("fits")}</span> with a movement that fits ·{" "}
              <span className="font-medium text-amber-700 dark:text-amber-400">
                {count("no_movement")}
              </span>{" "}
              with nothing in the bank
            </p>
          )}
          {open.length > 0 && (
            <table className="w-full text-sm">
              <thead className="text-left text-muted-foreground">
                <tr>
                  <th className="py-1 pr-4 font-medium">Paid</th>
                  <th className="py-1 pr-4 font-medium">Document</th>
                  <th className="py-1 pr-4 text-right font-medium">Total</th>
                  <th className="py-1 font-medium">In the bank</th>
                </tr>
              </thead>
              <tbody>
                {open.map((row) => (
                  <tr key={`${row.kind}-${row.id}`} className="border-t align-top">
                    <td className="py-1 pr-4 text-muted-foreground">
                      {row.paidOn ? <LocalDate value={row.paidOn} /> : "—"}
                    </td>
                    <td className="py-1 pr-4">
                      {row.kind === "received" ? "Expense" : "Invoice"} {row.number}
                      <div className="text-muted-foreground text-xs">{row.partnerName}</div>
                    </td>
                    <td className="py-1 pr-4 text-right tabular-nums">
                      {money(row.total, row.currency)}
                    </td>
                    <td className="py-1">
                      {row.verdict === "no_movement" ? (
                        <Badge variant="outline">nothing fits</Badge>
                      ) : (
                        <ul className="space-y-1">
                          {row.movements.map((m) => (
                            <li key={m.id} className="flex flex-wrap items-center gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                disabled={tie.pending}
                                onClick={() => tie.mutate({ row, movementId: m.id })}
                              >
                                Tie
                              </Button>
                              <LocalDate value={m.bookedOn} /> · {money(m.amount, m.currency)}
                              {m.text && (
                                <span className="text-muted-foreground text-xs">{m.text}</span>
                              )}
                            </li>
                          ))}
                        </ul>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {tie.error && <p className="text-destructive text-sm">{tie.error}</p>}
        </CardContent>
      )}
    </Card>
  );
}

/**
 * What the last pull did.
 *
 * "I clicked Pull now and nothing happened" truthfully describes three
 * different things: no account with a token, Fio returned nothing, or
 * the run failed. The page has to tell them apart itself — looking into
 * /sync
 * nikdo nebude.
 */
function LastPull({ status, accountCount }: { status: BankStatus; accountCount: number }) {
  const run = status.lastRun;

  if (status.accountsWithToken === 0) {
    return (
      <p className="rounded-md bg-amber-50 p-3 text-amber-900 text-sm dark:bg-amber-950/40 dark:text-amber-200">
        {accountCount === 0
          ? "There is no bank account yet — add one below together with its Fio token. Until then a pull has nothing to do."
          : "No account has a token, so a pull has nowhere to read from. Add one on an account below."}
      </p>
    );
  }

  if (!run) {
    return (
      <p className="text-muted-foreground text-sm">
        No pull has run yet. The scheduled one runs every 30 minutes.
      </p>
    );
  }

  if (run.status === "failed") {
    return (
      <p className="rounded-md bg-destructive/10 p-3 text-destructive text-sm">
        The last pull failed: {run.error ?? "no details"}
      </p>
    );
  }

  if (run.status === "running" || run.status === "pending") {
    return <p className="text-muted-foreground text-sm">Stahuji…</p>;
  }

  const result = run.result ?? {};
  const skipped = result.skipped ?? [];

  return (
    <div className="space-y-1 rounded-md border p-3 text-sm">
      <div>
        Last pull {run.finishedAt && <LocalDate value={run.finishedAt} withTime />} — imported{" "}
        <strong>{result.imported ?? 0}</strong> movement(s), of which{" "}
        <strong>{result.matched ?? 0}</strong> matched themselves.
      </div>
      {(result.imported ?? 0) === 0 && skipped.length === 0 && (
        // An empty result is a normal state, not an error — but it has to
        // be said, otherwise it looks like a fault.
        <div className="text-muted-foreground">
          Nothing new. Fio only returns movements Hub doesn't have yet.
        </div>
      )}
      {skipped.map((entry) => (
        <div key={`${entry.account}-${entry.reason}`} className="text-amber-600">
          {entry.account}: {entry.reason}
        </div>
      ))}
    </div>
  );
}

/** The token is never shown, not even shortened — the only thing that
 *  can be said about it is whether it is set. It can be replaced at any
 *  time (Fio invalidates it after 180 days), which overwrites the old
 *  one. */
function TokenCell({
  account,
  onDone,
}: {
  account: BankAccountView;
  onDone: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [token, setToken] = useState("");

  const save = useApiMutation(
    (value: string) =>
      api.patch(`/api/accounting/bank/accounts/${account.id}`, { fioToken: value }),
    {
      onSuccess: () => {
        setToken("");
        setEditing(false);
        onDone();
      },
      errorMessage: () => "Couldn't save the token.",
    },
  );

  if (!editing) {
    return (
      <div className="flex items-center gap-2">
        {account.hasToken ? (
          <Badge variant="success">set</Badge>
        ) : (
          <Badge variant="outline">missing</Badge>
        )}
        <button
          type="button"
          className="text-muted-foreground text-xs underline"
          onClick={() => setEditing(true)}
        >
          {account.hasToken ? "replace" : "add"}
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1">
      <input
        type="password"
        autoComplete="off"
        value={token}
        onChange={(e) => setToken(e.target.value)}
        placeholder="Fio token"
        className="h-8 w-44 rounded-md border border-input bg-background px-2 font-mono text-xs"
      />
      <Button
        size="sm"
        disabled={token.trim().length < 16 || save.pending}
        onClick={() => save.mutate(token.trim())}
      >
        Save
      </Button>
      <button
        type="button"
        className="text-muted-foreground text-xs underline"
        onClick={() => {
          setToken("");
          setEditing(false);
        }}
      >
        cancel
      </button>
      {save.error && <span className="text-destructive text-xs">{save.error}</span>}
    </div>
  );
}

function MovementRow({
  movement,
  isOpen,
  onToggle,
  onDone,
}: {
  movement: Transaction;
  isOpen: boolean;
  onToggle: () => void;
  onDone: () => void;
}) {
  const [candidates, setCandidates] = useState<Candidate[] | null>(null);
  const [reason, setReason] = useState("");

  // Suggestions are only worth fetching for the movement a person
  // actually opened.
  useEffect(() => {
    if (!isOpen || candidates !== null) return;
    api
      .get<{ candidates: Candidate[] }>(
        `/api/accounting/bank/transactions/${movement.id}/candidates`,
      )
      .then((res) => setCandidates(res.candidates))
      .catch(() => setCandidates([]));
  }, [isOpen, candidates, movement.id]);

  const place = useApiMutation(
    (candidate: Candidate) =>
      api.post(
        `/api/accounting/bank/transactions/${movement.id}/match`,
        candidate.kind === "received"
          ? { receivedInvoiceId: candidate.id }
          : { issuedInvoiceId: candidate.id },
      ),
    { onSuccess: onDone, errorMessage: () => "Couldn't place the payment." },
  );

  const revert = useApiMutation(
    (matchId: string) => api.post(`/api/accounting/bank/matches/${matchId}/revert`, {}),
    { onSuccess: onDone, errorMessage: () => "Couldn't take the match back." },
  );

  const [always, setAlways] = useState(false);
  const putAside = useApiMutation(
    (why: string) =>
      api.post(`/api/accounting/bank/transactions/${movement.id}/ignore`, {
        reason: why,
        always,
      }),
    { onSuccess: onDone, errorMessage: () => "Couldn't put the movement aside." },
  );

  const bringBack = useApiMutation(
    () => api.post(`/api/accounting/bank/transactions/${movement.id}/unignore`, {}),
    { onSuccess: onDone, errorMessage: () => "Couldn't bring the movement back." },
  );

  const incoming = Number(movement.amount) > 0;

  return (
    <>
      <tr className="border-t">
        <td className="py-1 pr-4 text-muted-foreground">
          <LocalDate value={movement.bookedOn} />
        </td>
        <td className="py-1 pr-4">
          {movement.counterpartyName ??
            (movement.card ? (
              <span className="flex items-center gap-1">
                {movement.card.merchant}
                <Badge variant="outline" className="text-[10px]">
                  card
                </Badge>
              </span>
            ) : (
              (movement.userIdentification ??
              (movement.counterpartyAccount ? (
                <span className="font-mono text-xs">
                  {movement.counterpartyAccount}
                  {movement.counterpartyBankCode && `/${movement.counterpartyBankCode}`}
                </span>
              ) : (
                "—"
              )))
            ))}
          {movement.message && (
            <div className="text-muted-foreground text-xs">{movement.message}</div>
          )}
        </td>
        <td className="py-1 pr-4 font-mono text-xs">{movement.variableSymbol ?? "—"}</td>
        <td
          className={`py-1 pr-4 text-right tabular-nums ${incoming ? "text-emerald-600" : "text-muted-foreground"}`}
        >
          {money(movement.amount, movement.currency)}
          {movement.card && movement.card.currency !== movement.currency && (
            <div className="text-muted-foreground text-xs">
              {money(movement.card.amount, movement.card.currency)}
            </div>
          )}
        </td>
        <td className="py-1">
          {movement.matches.length > 0 ? (
            <div className="flex flex-wrap items-center gap-2">
              {movement.matches.map((m) => (
                <span key={m.id} className="flex items-center gap-1">
                  <Badge variant={m.source === "auto" ? "secondary" : "outline"}>
                    {m.issuedInvoice?.number ??
                      m.receivedInvoice?.number ??
                      m.receivedInvoice?.internalNumber ??
                      m.receivedInvoice?.partnerName ??
                      "?"}{" "}
                    ·{" "}
                    {m.source === "fakturoid"
                      ? "z Fakturoidu"
                      : m.source === "auto"
                        ? "automaticky"
                        : "by hand"}
                    {m.settles ? "" : " · jen vazba"}
                  </Badge>
                  <button
                    type="button"
                    className="text-muted-foreground text-xs underline"
                    disabled={revert.pending}
                    onClick={() => revert.mutate(m.id)}
                  >
                    undo
                  </button>
                </span>
              ))}
            </div>
          ) : movement.ignoredAt ? (
            <div className="flex items-center gap-2">
              <Badge variant="outline">
                {movement.ignoredByRuleId ? "put aside · rule" : "put aside"}
              </Badge>
              <span className="text-muted-foreground text-xs">{movement.ignoreReason}</span>
              <button
                type="button"
                className="text-muted-foreground text-xs underline"
                disabled={bringBack.pending}
                onClick={() => bringBack.mutate()}
              >
                bring back
              </button>
            </div>
          ) : (
            <Button variant="ghost" size="sm" onClick={onToggle}>
              {isOpen ? "Close" : "Decide…"}
            </Button>
          )}
        </td>
      </tr>

      {isOpen && (
        <tr className="border-t bg-muted/30">
          <td colSpan={5} className="space-y-3 p-3">
            <div>
              <div className="mb-1 text-muted-foreground text-xs">Could be</div>
              {candidates === null ? (
                <p className="text-muted-foreground text-sm">Looking…</p>
              ) : candidates.length === 0 ? (
                <p className="text-muted-foreground text-sm">
                  Nothing looks like a match — put the movement aside if it isn't a document
                  payment.
                </p>
              ) : (
                <ul className="space-y-1">
                  {candidates.map((candidate) => (
                    <li key={candidate.id} className="flex flex-wrap items-center gap-2">
                      <Button
                        size="sm"
                        disabled={place.pending}
                        onClick={() => place.mutate(candidate)}
                      >
                        {candidate.status === "paid" ? "Tie to" : "Place on"} {candidate.number}
                      </Button>
                      <span className="text-sm">
                        {candidate.partnerName} · {money(candidate.total, candidate.currency)}
                      </span>
                      {candidate.reasons.map((r) => (
                        <Badge key={r} variant="secondary" className="text-[10px]">
                          {REASON_LABEL[r]}
                        </Badge>
                      ))}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-2 border-t pt-3">
              <input
                type="text"
                placeholder="Not a payment? Say why — bank fee, transfer, salary…"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className="h-9 min-w-[18rem] flex-1 rounded-md border border-input bg-background px-2 text-sm"
              />
              {(movement.counterpartyAccount || (movement.card && Number(movement.amount) < 0)) && (
                <label
                  className="flex items-center gap-1 text-xs"
                  htmlFor={`always-${movement.id}`}
                >
                  <input
                    id={`always-${movement.id}`}
                    type="checkbox"
                    checked={always}
                    onChange={(e) => setAlways(e.target.checked)}
                    className="h-4 w-4"
                  />
                  {movement.card
                    ? `always for card charges at ${movement.card.merchant}`
                    : `always for ${movement.counterpartyAccount}/${movement.counterpartyBankCode}`}
                </label>
              )}
              <Button
                variant="outline"
                size="sm"
                disabled={reason.trim().length < 2 || putAside.pending}
                onClick={() => putAside.mutate(reason.trim())}
              >
                Put aside
              </Button>
              {(place.error || putAside.error || revert.error) && (
                <span className="text-destructive text-sm">
                  {place.error ?? putAside.error ?? revert.error}
                </span>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

type IgnoreRule = {
  id: string;
  counterpartyAccount: string | null;
  counterpartyBankCode: string | null;
  cardMerchant: string | null;
  reason: string;
  putAside: number;
};

/** Accounts whose payments are never a document payment: taxes, donations. */
function IgnoreRules() {
  const router = useRouter();
  const [rules, setRules] = useState<IgnoreRule[] | null>(null);
  const load = useCallback(
    () =>
      api
        .get<{ rules: IgnoreRule[] }>("/api/accounting/bank/ignore-rules")
        .then((r) => setRules(r.rules))
        .catch(() => setRules([])),
    [],
  );
  useEffect(() => {
    load();
  }, [load]);
  const remove = useApiMutation(
    (id: string) => api.delete(`/api/accounting/bank/ignore-rules/${id}`),
    {
      onSuccess: async () => {
        await load();
        router.invalidate();
      },
      errorMessage: () => "Couldn't remove the rule.",
    },
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle>Put aside automatically</CardTitle>
        <CardDescription>
          Payments to these accounts or card charges at these merchants are never a document
          payment. A new rule comes from ticking „always“ when putting a movement aside. Removing a
          rule brings back what it put aside.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {rules === null ? (
          <p className="text-muted-foreground text-sm">Loading…</p>
        ) : rules.length === 0 ? (
          <p className="text-muted-foreground text-sm">No rules.</p>
        ) : (
          <ul className="space-y-1 text-sm">
            {rules.map((rule) => (
              <li key={rule.id} className="flex flex-wrap items-center gap-2">
                <span className="font-mono text-xs">
                  {rule.cardMerchant
                    ? `card: ${rule.cardMerchant}`
                    : `${rule.counterpartyAccount ?? "any account"}/${rule.counterpartyBankCode}`}
                </span>
                <span>{rule.reason}</span>
                <span className="text-muted-foreground text-xs">{rule.putAside} put aside</span>
                <button
                  type="button"
                  className="text-muted-foreground text-xs underline"
                  disabled={remove.pending}
                  onClick={() => remove.mutate(rule.id)}
                >
                  remove
                </button>
              </li>
            ))}
          </ul>
        )}
        {remove.error && <p className="text-destructive text-sm">{remove.error}</p>}
      </CardContent>
    </Card>
  );
}

type RebuildState = {
  run: { id: string; year: number; createdAt: string; documents: number } | null;
  unconfirmed: Array<{
    kind: "issued" | "received";
    id: string;
    number: string;
    partnerName: string;
    total: string;
    paidInHub: string;
    currency: string;
    issuedOn: string;
    fakturoidPayments: Array<{ paidOn: string; amount: string }>;
    bankCandidates: Array<{
      transactionId: string;
      bookedOn: string;
      amount: string;
      currency: string;
      payee: string | null;
      card: { merchant: string; amount: string; currency: string } | null;
      reasons: string[];
      parts: Array<{ transactionId: string }> | null;
    }>;
  }>;
  accepted: Array<{
    kind: "issued" | "received";
    id: string;
    number: string;
    partnerName: string;
    total: string;
    currency: string;
  }>;
};

/**
 * Fakturoid's pairing can't be trusted: the bank decides what is paid.
 * Fakturoid itself is left as it is.
 */
function RebuildFromBank() {
  const router = useRouter();
  const [year, setYear] = useState(new Date().getFullYear());
  const [state, setState] = useState<RebuildState | null>(null);
  const load = useCallback(
    () =>
      api
        .get<RebuildState>("/api/accounting/bank/rebuild")
        .then(setState)
        .catch(() => setState({ run: null, unconfirmed: [], accepted: [] })),
    [],
  );
  useEffect(() => {
    load();
  }, [load]);
  const after = async () => {
    await load();
    router.invalidate();
  };

  const rebuild = useApiMutation(() => api.post("/api/accounting/bank/rebuild", { year }), {
    onSuccess: after,
    errorMessage: () => "Couldn't rebuild the payments.",
  });
  const accept = useApiMutation(
    ({ runId, kind, documentId }: { runId: string; kind: string; documentId: string }) =>
      api.post(`/api/accounting/bank/rebuild/${runId}/accept`, { kind, documentId }),
    { onSuccess: after, errorMessage: () => "Couldn't take Fakturoid's payment." },
  );
  const unaccept = useApiMutation(
    ({ runId, kind, documentId }: { runId: string; kind: string; documentId: string }) =>
      api.post(`/api/accounting/bank/rebuild/${runId}/unaccept`, { kind, documentId }),
    { onSuccess: after, errorMessage: () => "Couldn't take it back." },
  );
  const pair = useApiMutation(
    async ({ kind, documentId, ids }: { kind: string; documentId: string; ids: string[] }) => {
      for (const id of ids) {
        await api.post(`/api/accounting/bank/transactions/${id}/match`, {
          [kind === "issued" ? "issuedInvoiceId" : "receivedInvoiceId"]: documentId,
        });
      }
    },
    { onSuccess: after, fallbackMessage: "Couldn't pair the payment." },
  );
  const undo = useApiMutation(
    (runId: string) => api.post(`/api/accounting/bank/rebuild/${runId}/revert`, {}),
    { onSuccess: after, errorMessage: () => "Couldn't undo the rebuild." },
  );

  const run = state?.run;
  return (
    <Card>
      <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-4">
        <div>
          <CardTitle>Payments from the bank</CardTitle>
          <CardDescription>
            Documents imported from Fakturoid get their payments from the bank statement instead of
            Fakturoid's pairing. Fakturoid itself stays as it is. What Fakturoid called paid and the
            bank doesn't show is listed below.
          </CardDescription>
        </div>
        <div className="flex items-center gap-2">
          <input
            type="number"
            aria-label="Rebuild year"
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            className="h-9 w-24 rounded-md border border-input bg-background px-2 text-sm"
          />
          <Button
            variant="outline"
            size="sm"
            disabled={rebuild.pending}
            onClick={() => {
              if (window.confirm(`Rebuild ${year} payments from the bank? It can be undone.`)) {
                rebuild.mutate();
              }
            }}
          >
            {rebuild.pending ? "Rebuilding…" : "Rebuild from the bank"}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        {run ? (
          <div className="flex flex-wrap items-center gap-2">
            <span>
              Last rebuild of {run.year}: <LocalDate value={run.createdAt} withTime />,{" "}
              {run.documents} document(s).
            </span>
            <button
              type="button"
              className="text-muted-foreground text-xs underline"
              disabled={undo.pending}
              onClick={() => {
                if (window.confirm("Undo the rebuild and return to Fakturoid's payments?")) {
                  undo.mutate(run.id);
                }
              }}
            >
              undo the rebuild
            </button>
          </div>
        ) : (
          <p className="text-muted-foreground">Not rebuilt yet.</p>
        )}
        {run && state && state.unconfirmed.length > 0 && (
          <>
            <p className="font-medium">
              Fakturoid called these paid, the bank shows nothing ({state.unconfirmed.length})
            </p>
            <table className="w-full">
              <thead className="text-left text-muted-foreground">
                <tr>
                  <th className="py-1 pr-4 font-medium">Document</th>
                  <th className="py-1 pr-4 text-right font-medium">Total</th>
                  <th className="py-1 pr-4 font-medium">Fakturoid's payment</th>
                  <th className="py-1 font-medium" />
                </tr>
              </thead>
              <tbody>
                {state.unconfirmed.map((doc) => (
                  <tr key={`${doc.kind}-${doc.id}`} className="border-t align-top">
                    <td className="py-1 pr-4">
                      {doc.kind === "received" ? "Expense" : "Invoice"} {doc.number}
                      <div className="text-muted-foreground text-xs">{doc.partnerName}</div>
                    </td>
                    <td className="py-1 pr-4 text-right tabular-nums">
                      {money(doc.total, doc.currency)}
                    </td>
                    <td className="py-1 pr-4 text-xs">
                      {doc.fakturoidPayments.map((p) => (
                        <div key={`${p.paidOn}-${p.amount}`}>
                          <LocalDate value={p.paidOn} /> · {money(p.amount, doc.currency)}
                        </div>
                      ))}
                    </td>
                    <td className="space-y-1 py-1">
                      {doc.bankCandidates.length === 0 && (
                        <p className="text-muted-foreground text-xs">
                          No movement in the bank fits.
                        </p>
                      )}
                      {doc.bankCandidates.map((m) => (
                        <div key={m.transactionId} className="flex flex-wrap items-center gap-2">
                          <Button
                            size="sm"
                            disabled={pair.pending}
                            onClick={() =>
                              pair.mutate({
                                kind: doc.kind,
                                documentId: doc.id,
                                ids: m.parts?.map((p) => p.transactionId) ?? [m.transactionId],
                              })
                            }
                          >
                            Pair
                          </Button>
                          <span className="text-xs">
                            <LocalDate value={m.bookedOn} /> · {money(m.amount, m.currency)}
                            {m.parts && ` (${m.parts.length} payments)`} ·{" "}
                            {m.card ? m.card.merchant : (m.payee ?? "—")}
                          </span>
                        </div>
                      ))}
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={accept.pending}
                        onClick={() => {
                          if (
                            window.confirm(
                              "Keep Fakturoid's payment without a bank movement (cash, another account)?",
                            )
                          ) {
                            accept.mutate({ runId: run.id, kind: doc.kind, documentId: doc.id });
                          }
                        }}
                      >
                        Paid outside this account
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="text-muted-foreground text-xs">
              „Pair“ pays it from that bank movement. „Paid outside this account“ keeps Fakturoid's
              payment (cash, another card or account). The rest stays unpaid.
            </p>
          </>
        )}
        {run && state && state.accepted.length > 0 && (
          <div className="space-y-1">
            <p className="font-medium">Kept at Fakturoid's payment ({state.accepted.length})</p>
            {state.accepted.map((doc) => (
              <div key={`${doc.kind}-${doc.id}`} className="flex flex-wrap items-center gap-2">
                <span>
                  {doc.kind === "received" ? "Expense" : "Invoice"} {doc.number} · {doc.partnerName}{" "}
                  · {money(doc.total, doc.currency)}
                </span>
                <button
                  type="button"
                  className="text-muted-foreground text-xs underline"
                  disabled={unaccept.pending}
                  onClick={() =>
                    unaccept.mutate({ runId: run.id, kind: doc.kind, documentId: doc.id })
                  }
                >
                  undo
                </button>
              </div>
            ))}
          </div>
        )}
        {(rebuild.error || accept.error || undo.error || unaccept.error || pair.error) && (
          <p className="text-destructive">
            {rebuild.error ?? accept.error ?? undo.error ?? unaccept.error ?? pair.error}
          </p>
        )}
      </CardContent>
    </Card>
  );
}
